<template>
  <div ref="scrollBox" style="overflow-y: scroll; height: 100vh;">

  <div class="certificate-verification">
    <!-- Loader -->
    <div v-if="loading" class="loader-wrapper">
      <div class="loader">
        <img src="../assets/unodc_logo2.png" alt="UNODC Logo" />
      </div>
      <div class="loader-text">Fetching certificate information...</div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="error-icon">!</div>
      <h2>No Record Found</h2>
      <button @click="fetchData" class="retry-btn">Retry</button>
    </div>

    <!-- Main Content -->
    <div v-else class="main-container" :class="{ visible: containerVisible }">
      <!-- Logo -->
      <div class="logo-top">
        <img src="../assets/unodc_logo2.png" alt="Logo" />
        <!-- <div class="verified-tag">VERIFIED</div> -->
      </div>

      <!-- Page Title -->
      <div class="page-header">
        <h1>Certificate Verification</h1>
        <p>Verify Training Certificate Authenticity</p>
      </div>

      <!-- Training Title in Blue Box -->
      <div class="header-box">
        <div class="training-title">
          {{ trainingData.title }}
        </div>
      </div>

      <!-- Typing Content -->
      <div class="content-box">
        <div v-for="(line, index) in typedLines" 
             :key="index" 
             :class="['info-line', { 'show': line.show }]"
             v-html="line.content">
        </div>
      </div>

      <!-- Footer -->
      <footer class="certificate-footer" :class="{ 'show': footerVisible }">
        <div class="verification-info">
          <i class="fas fa-shield-alt"></i>
          UNODC COPAK Certificate Verification Service (M&E UNIT)
        </div>
      </footer>
    </div>
  </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CertificateVerification',
  data() {
    return {
      eventId: '',
      traineeId: '',
      trainingId: '',
      trainingData: {},
      participantInfo: {},
      loading: true,
      error: null,
      containerVisible: false,
      footerVisible: false,
      typedLines: [],
      allLines: []
    };
  },
  created() {
    this.extractIdsFromUrl();
    this.fetchData();
  },
  methods: {
    extractIdsFromUrl() {
      const query = this.$route.query;
      this.eventId = query.eventId || '';
      this.traineeId = query.traineeId || '';
      this.trainingId = query.trainingId || '';
    },
    
    async fetchData() {
      this.loading = true;
      this.error = null;
      
      try {
        const [trainingResponse, participantResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_BASE_PATH}/trainee/training/single/${this.eventId}/get`),
          axios.get(`${process.env.VUE_APP_BASE_PATH}/get-trainee-info/${this.trainingId}/${this.traineeId}`)
        ]);
        
        this.trainingData = trainingResponse.data;
        this.participantInfo = participantResponse.data?.success || {};
        
        if (!this.trainingData || !this.participantInfo) {
          throw new Error("Invalid certificate data received");
        }

        this.prepareLines();
        this.showContent();
      } catch (error) {
        console.error("Error fetching data:", error);
        this.error = "Failed to load certificate details. Please check the URL or try again later.";
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    
    prepareLines() {
      this.allLines = [
        `<div class="section"><h3 style="color: #06a6e0; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Personal Information</h3></div>`,
        
        `<div><span style="font-weight: bold;">Full Name:</span> ${this.participantInfo?.trainee?.userstrainee?.name}<i class="fas fa-check-circle ml-1" style="color: #28a745;"></i></div>`,
        `<div><span style="font-weight: bold;">Designation:</span> ${this.participantInfo?.trainee?.designationT?.title || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">Gender:</span> ${this.formatGender(this.participantInfo?.trainee?.userstrainee?.gender) || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">Department:</span> ${this.participantInfo?.trainee?.department?.title || 'N/A'}</div>`,
        `<div class="section"><h3 style="color: #06a6e0; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Event Information</h3></div>`,
        `<div><span style="font-weight: bold;">Dates:</span> ${this.formatDate(this.trainingData.startdate)} - ${this.formatDate(this.trainingData.enddate)}</div>`,
        `<div><span style="font-weight: bold;">Type:</span> ${this.trainingData.trainingtype?.title || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">Venue:</span> ${this.trainingData.venue || 'N/A'}</div>`,
        `<div class="section"><h3 style="color: #06a6e0; border-bottom: 1px solid #ddd; padding-bottom: 5px;">Additional Information</h3></div>`,
        `<div><span style="font-weight: bold;">Project:</span> ${this.trainingData.project?.title || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">Donor:</span> ${this.trainingData.doners || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">Subprogramme:</span> ${this.trainingData.subprogram?.title || 'N/A'}</div>`,
        `<div><span style="font-weight: bold;">District:</span> ${this.trainingData.district?.title || 'N/A'}</div>`
      ];
    },
    
    showContent() {
      setTimeout(() => {
        this.loading = false;
        this.containerVisible = true;
        
        setTimeout(() => {
          this.typeLines();
        }, 600);
      }, 1000);
    },
    formatGender(gender) {
       if (!gender) return null;
       return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
     },
    typeLines(index = 0) {
      if (index >= this.allLines.length) {
        this.footerVisible = true;
        return;
      }
      
      this.typedLines.push({
        content: this.allLines[index],
        show: false
      });
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.typedLines[this.typedLines.length - 1].show = true;
          this.typeLines(index + 1);
        }, 200);
      });
    }
  }
};
</script>

<style scoped>
.certificate-verification {
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f4f4f4;
  color: #333;
  
}

.loader-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.loader {
  width: 120px;
  height: 120px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader::before {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  border: 8px solid #f3f3f3;
  border-top: 8px solid #06a6e0;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 1;
}

.loader img {
  position: relative;
  z-index: 2;
  width: 60px;
  height: auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loader-text {
  margin-top: 20px;
  font-size: 1rem;
  color: #06a6e0;
}

.error-container {
  text-align: center;
  padding: 30px;
  background-color: #fdecea;
  border-radius: 8px;
  color: #d32f2f;
  max-width: 600px;
  margin: 20px auto;
}

.error-icon {
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 15px;
}

.retry-btn {
  background-color: #d32f2f;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
  transition: background-color 0.3s;
}

.retry-btn:hover {
  background-color: #b71c1c;
}

.main-container {
  max-width: 600px;
  margin: 0px auto;
  background: #fff;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: relative;
  opacity: 0;
  transition: opacity 1s ease-in;
  margin-bottom: 45px;
}

.main-container.visible {
  opacity: 1;
}

.logo-top {
  text-align: center;
  padding: 4px 10px 0;
  position: relative;
}

.logo-top img {
  max-width: 80px;
}

.verified-tag {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #28a745;
  color: white;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.page-header {
  text-align: center;
  padding: 10px 10px 5px;
}

.page-header h1 {
  margin: 5px 0 0;
  font-size: 1.5rem;
  font-weight: bold;
}

.page-header p {
  font-size: 0.95rem;
  color: #555;
  margin-top: 5px;
}

.header-box {
  background-color: #06a6e0;
  padding: 20px;
  text-align: center;
  color: white;
}

.header-box .training-title {
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.4;
}

.content-box {
  padding: 0px 20px;
  
}

.section {
  margin-bottom: 20px;
}

.section h3 {
  color: #06a6e0;
  font-size: 1rem;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 5px;
}

.info-line {
  margin: 6px 0;
  font-size: 0.95rem;
  opacity: 0;
  transition: opacity 0.3s ease-in;
}

.info-line.show {
  opacity: 1;
}

.info-line span {
  font-weight: bold;
}

.green-tick {
  color: #28a745;
  margin-left: 4px;
}

.certificate-footer {
  text-align: center;
  padding: 10px 0;
  border-top: 1px solid #ccc;
  font-size: 11px;
  background-color: #fff;
  border-radius: 0 0 10px 10px;
  opacity: 0;
  transition: opacity 1s ease-in;
}

.certificate-footer i {
  color: #06a6e0;
  margin-right: 6px;
}

.certificate-footer.show {
  opacity: 1;
}

@media screen and (max-width: 480px) {
  .header-box .training-title {
    font-size: 1rem;
  }
  
  .page-header h1 {
    font-size: 1.3rem;
  }
  
  .page-header p {
    font-size: 0.85rem;
  }
  
  .info-line {
    font-size: 0.9rem;
  }
  .main-container {
  
  border-radius: 0px 0px 15px 15px;
  
}
}


</style>